import { forwardRef } from 'react';
import type { PropsWithoutRef, MouseEvent } from 'react';

import type { NoChildrenProp, RefElement, WithAttributes } from '../../types';
import Button from '../Button';
import { useElement, useI18n } from '../../hooks';
import InfoDialog from '../Dialog/InfoDialog';
import type { InfoDialogProps } from '../Dialog';
import NoValue from '../NoValue';

export type CompactListProps = WithAttributes<
  'div',
  NoChildrenProp &
    Pick<InfoDialogProps, 'progress'> & {
      /** number representing the total count of items */
      count: number;
      heading: InfoDialogProps['heading'];
      /** Callback that triggers when the dialog is open */
      onDialogOpen?: () => void;
      /** Callback that triggers when the dialog is closed */
      onDialogClose?: () => void;
      content?: InfoDialogProps['children'];
    }
>;

const CompactList = forwardRef<RefElement<CompactListProps>, PropsWithoutRef<CompactListProps>>(
  function CompactList(
    { count, onDialogOpen, onDialogClose, progress, heading, content, ...restProps },
    ref
  ) {
    const t = useI18n();
    const [target, setTarget] = useElement<HTMLButtonElement>(null);

    return (
      <div {...restProps} ref={ref}>
        {count === 0 ? (
          <NoValue />
        ) : (
          <>
            <Button
              variant='link'
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                if (onDialogOpen && !target) onDialogOpen();
                setTarget(e.currentTarget);
              }}
            >
              {t('number_of_items', [count], { count })}
            </Button>

            {target && (
              <InfoDialog
                heading={heading}
                progress={progress}
                target={target}
                onDismiss={() => {
                  if (onDialogClose) onDialogClose();
                  setTarget(null);
                }}
              >
                {content}
              </InfoDialog>
            )}
          </>
        )}
      </div>
    );
  }
);

export default CompactList;
